import React, { useEffect, useRef, useState } from "react"

import { NotificationManager } from "react-notifications"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"

import createBlob from "src/createBlob"
import client from "src/urql-client"

type TranscriptDownloadProps = {
  sessionId: string
}

const TranscriptDownload: React.FC<TranscriptDownloadProps> = ({
  sessionId,
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [transcriptUrl, setTranscriptUrl] = useState<string | null>(null)
  const downloadLink = useRef<HTMLAnchorElement>(null)

  const handleDownload = () => {
    setIsDownloading(true)

    const { unsubscribe } = pipe(
      client.subscription(generateTranscriptSubscription, { sessionId }),
      subscribe(result => {
        if (result.data) {
          const { status, fileContent, failures } =
            result.data.generateTranscriptJob

          if (failures && failures.length > 0) {
            NotificationManager.error(failures[0].message)
            setIsDownloading(false)
            unsubscribe()
            return
          }

          if (status === "done") {
            const blobUrl = createBlob(fileContent, "text/plain")
            setTranscriptUrl(blobUrl)
            setIsDownloading(false)
            unsubscribe()
          }
        } else {
          const errorMessage = result.error?.message || "Something went wrong"
          NotificationManager.error(errorMessage)
          setIsDownloading(false)
          unsubscribe()
          return
        }
      })
    )
  }

  useEffect(() => {
    if (!transcriptUrl) return
    downloadLink.current?.click()
    setTranscriptUrl(null)
    setTimeout(() => URL.revokeObjectURL(transcriptUrl), 0)
  }, [transcriptUrl])

  return (
    <>
      <a
        ref={downloadLink}
        href={transcriptUrl}
        download={`session-${sessionId}-transcript.txt`}
        className="hidden"
      >
        download
      </a>

      <button
        type="button"
        className="btn btn-sm btn-success w-52"
        onClick={handleDownload}
        disabled={isDownloading}
      >
        {isDownloading ? "Downloading..." : "Download Transcript"}
      </button>
    </>
  )
}

const generateTranscriptSubscription = gql`
  subscription GenerateTranscriptJob($sessionId: ID!) {
    generateTranscriptJob(sessionId: $sessionId) {
      status
      fileContent
      failures {
        message
      }
    }
  }
`

export default TranscriptDownload
